import { useState, useCallback } from 'react';

type AttachmentStatus = {
  label: string;
  extraLabel?: string;
  color: string;
  icon: string;
  locked: boolean;
  cost?: number;
  hasVipPrivilege?: boolean;
};

interface ToolAttachment {
  id?: string | number;
  name: string;
  size: string;
  url: string;
  downloadUrl?: string | null;
  downloadType?: string;
  downloadTypes?: string[];
  lingshiCost?: number;
  timedFreeUntil?: string;
}

interface AttachmentPanelProps {
  attachments: ToolAttachment[];
  checkAndDownload: (
    attachment: ToolAttachment,
    attachIndex: number,
    onSuccess: (url: string) => void,
    onRequireLogin: () => void,
  ) => Promise<void>;
  getAttachmentStatus: (attachment: ToolAttachment, attachIndex: number) => AttachmentStatus;
  onRequireLogin: () => void;
  onPdfPreview?: (previewUrl: string, name: string, downloadUrl?: string | null) => void;
  accentColor?: string;
}

function getExtInfo(name: string): { icon: string; color: string; bg: string; label: string } {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, { icon: string; color: string; bg: string; label: string }> = {
    pdf: { icon: 'ri-file-pdf-2-line', color: '#ef4444', bg: '#fef2f2', label: 'PDF' },
    doc: { icon: 'ri-file-word-2-line', color: '#0ea5e9', bg: '#e0f2fe', label: 'Word' },
    docx: { icon: 'ri-file-word-2-line', color: '#0ea5e9', bg: '#e0f2fe', label: 'Word' },
    xls: { icon: 'ri-file-excel-2-line', color: '#10b981', bg: '#ecfdf5', label: 'Excel' },
    xlsx: { icon: 'ri-file-excel-2-line', color: '#10b981', bg: '#ecfdf5', label: 'Excel' },
    zip: { icon: 'ri-file-zip-line', color: '#f97316', bg: '#fff7ed', label: 'ZIP' },
    tar: { icon: 'ri-file-zip-line', color: '#f97316', bg: '#fff7ed', label: 'TAR' },
    sh: { icon: 'ri-terminal-line', color: '#6366f1', bg: '#eef2ff', label: 'Shell' },
    yml: { icon: 'ri-code-line', color: '#f59e0b', bg: '#fffbeb', label: 'YAML' },
    yaml: { icon: 'ri-code-line', color: '#f59e0b', bg: '#fffbeb', label: 'YAML' },
    md: { icon: 'ri-markdown-line', color: '#6b7280', bg: '#f3f4f6', label: 'MD' },
  };
  return map[ext] || { icon: 'ri-file-line', color: '#6b7280', bg: '#f3f4f6', label: '文件' };
}

function openAttachmentUrl(url: string, fileName?: string) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface DownloadBtnProps {
  locked: boolean;
  status: AttachmentStatus;
  accentColor: string;
  onDownload: () => void;
}

function DownloadBtn({ locked, status, accentColor, onDownload }: DownloadBtnProps) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = useCallback(() => {
    if (downloading) return;
    if (!locked) {
      setDownloading(true);
      setTimeout(() => setDownloading(false), 1200);
    }
    onDownload();
  }, [locked, onDownload, downloading]);

  if (downloading) {
    return (
      <button
        disabled
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium min-w-[68px] justify-center whitespace-nowrap"
        style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
      >
        <span
          className="w-3.5 h-3.5 border-2 rounded-full animate-spin shrink-0"
          style={{ borderColor: `${accentColor}40`, borderTopColor: accentColor }}
        ></span>
        下载中
      </button>
    );
  }

  if (locked) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer whitespace-nowrap min-w-[68px] justify-center transition-all"
      >
        <i className="ri-lock-line text-xs"></i>
        {status.cost ? `${status.cost} 灵石` : '需权限'}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white hover:opacity-90 cursor-pointer whitespace-nowrap min-w-[68px] justify-center transition-all"
      style={{ backgroundColor: accentColor }}
    >
      <i className="ri-download-line text-xs"></i>
      下载
    </button>
  );
}

export default function ToolAttachmentPanel({
  attachments, checkAndDownload, getAttachmentStatus, onRequireLogin, onPdfPreview, accentColor = '#f97316'
}: AttachmentPanelProps) {
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const handleDownload = (att: ToolAttachment, idx: number) => {
    checkAndDownload(
      att as any,
      idx,
      (url) => {
        if (!url || url === '#' || url.startsWith('#')) {
          setDownloadNotice(`「${att.name}」未配置下载地址`);
          setTimeout(() => setDownloadNotice(null), 3000);
          return;
        }
        openAttachmentUrl(url, att.name);
      },
      onRequireLogin,
    );
  };

  if (attachments.length === 0) return null;

  const freeCount = attachments.filter((_, i) => !getAttachmentStatus(_ as any, i).locked).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {downloadNotice && (
        <div className="mx-5 mt-4 flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
          <i className="ri-information-line text-amber-500 shrink-0"></i>
          {downloadNotice}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accentColor}18` }}
          >
            <i className="ri-attachment-2 text-base" style={{ color: accentColor }}></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">附件下载</h3>
            <p className="text-xs text-gray-400 mt-0.5">{attachments.length} 个资源文件</p>
          </div>
        </div>
        <span
          className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
        >
          {freeCount} 免费
        </span>
      </div>

      {/* File list */}
      <ul className="divide-y divide-gray-50">
        {attachments.map((att, idx) => {
          const info = getExtInfo(att.name);
          const status = getAttachmentStatus(att as any, idx);
          const isDownloaded = status.label === '已购';
          const isPdf = att.name.toLowerCase().endsWith('.pdf');
          const previewUrl = att.fileObjectId ? `/api/v1/files/preview/${att.fileObjectId}` : att.url;
          const hasPdfUrl = Boolean(previewUrl && previewUrl.trim && previewUrl.trim() !== '#');

          return (
            <li key={idx} className="group hover:bg-gray-50/70 transition-colors">
              <div className="flex items-center gap-3 px-5 py-3.5">
                {/* Icon */}
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0 relative"
                  style={{ backgroundColor: info.bg }}
                >
                  <i className={`${info.icon} text-lg`} style={{ color: info.color }}></i>
                  {isDownloaded && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#10b981] rounded-full border-2 border-white">
                      <i className="ri-check-line text-white" style={{ fontSize: '8px' }}></i>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-800 truncate max-w-[160px]">{att.name}</p>
                    {isDownloaded && (
                      <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] font-medium whitespace-nowrap shrink-0">
                        <i className="ri-checkbox-circle-fill text-xs"></i>已下载
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-xs text-gray-400">{att.size || '无'}</span>
                    <span className="text-xs text-gray-300">·</span>
                    <span className="text-xs font-medium" style={{ color: info.color }}>{info.label}</span>

                    {!isDownloaded && (
                      <span
                        className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap shrink-0"
                        style={{ backgroundColor: `${status.color}18`, color: status.color }}
                      >
                        <i className={`${status.icon} text-xs`}></i>
                        {status.label}
                      </span>
                    )}

                    {status.hasVipPrivilege && !isDownloaded && (
                      <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 font-medium whitespace-nowrap shrink-0">
                        <i className="ri-vip-crown-line text-xs"></i>VIP免费
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {isPdf && onPdfPreview && hasPdfUrl && (
                    <button
                      onClick={() => onPdfPreview(previewUrl, att.name, att.downloadUrl || att.url)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                      title="在线预览"
                    >
                      <i className="ri-eye-line text-sm"></i>
                    </button>
                  )}
                  <DownloadBtn
                    locked={status.locked}
                    status={status}
                    accentColor={accentColor}
                    onDownload={() => handleDownload(att, idx)}
                  />
                </div>
              </div>

              {/* Locked indicator bar */}
              {status.locked && (
                <div className="mx-5 mb-2.5 -mt-0.5">
                  <div className="h-0.5 bg-gray-100 rounded-full">
                    <div className="h-full w-0 rounded-full" style={{ backgroundColor: status.color }}></div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-50">
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <i className="ri-information-line" style={{ color: accentColor }}></i>
          VIP 会员可免费下载标有 VIP 特权的附件，灵石消耗记录永久有效
        </p>
      </div>
    </div>
  );
}
